// src/api/chatApi.ts
import axiosClient from "./AxiosClient";
import { API_BASE_URL, ENHANCE_API_BASE_URL } from "../config";
import { acquireAccessToken } from "../utils/accessToken";

// Payload for first message (creates new session)
type CreateFirstMessagePayload = {
  project_id: string;
  session_name: string;
  question: string;
  ai_message: string;
  raw_message: string;
  chain_of_thought: string;
};

// Payload for subsequent messages (uses existing session)
type CreateSubsequentMessagePayload = {
  session_id: string;
  question: string;
  ai_message: string;
  raw_message: string;
  chain_of_thought: string;
};

type CreateMessagePayload =
  | CreateFirstMessagePayload
  | CreateSubsequentMessagePayload;

export const chatApi = {
  createMessage: async (payload: CreateMessagePayload) => {
    const url = `${API_BASE_URL.replace(/\/$/, "")}/messages/create`;
    const resp = await axiosClient.post(url, payload);
    return resp.data;
  },

  sendQuery: async (query: string, conversation_history: string[] = []) => {
    const url = `${ENHANCE_API_BASE_URL.replace(/\/$/, "")}/query`;
    const payload: any = { query, conversation_history };
    const resp = await axiosClient.post(url, payload);
    return resp.data;
  },

  sendQueryStream: async (
    query: string,
    conversation_history: string[] = [],
    onStep: (step: any) => void
  ) => {
    const url = `${ENHANCE_API_BASE_URL.replace(/\/$/, "")}/query_stream`;
    const token = await acquireAccessToken();

    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({ query, conversation_history }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error("Stream API error:", response.status, errorText);
      throw new Error(`HTTP error! status: ${response.status} - ${errorText}`);
    }

    const reader = response.body?.getReader();
    const decoder = new TextDecoder();

    if (!reader) {
      throw new Error("No response body");
    }

    let buffer = "";
    let finalResponse = null;

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split("\n");
      buffer = lines.pop() || "";

      for (const line of lines) {
        const trimmedLine = line.trim();
        if (trimmedLine) {
          try {
            // Handle Server-Sent Events format: "data: {json}"
            let jsonStr = trimmedLine;
            if (trimmedLine.startsWith("data:")) {
              jsonStr = trimmedLine.substring(5).trim();
            }

            if (jsonStr) {
              const stepData = JSON.parse(jsonStr);
              console.log("Parsed step:", stepData);
              onStep(stepData);
              if (stepData.step === "completed") {
                finalResponse = stepData;
              }
            }
          } catch (e) {
            console.error("Error parsing JSON:", e, "Line:", trimmedLine);
          }
        }
      }
    }

    return finalResponse;
  },

  getPromptQuestions: async (accessToken?: string) => {
    const url = `${ENHANCE_API_BASE_URL.replace(/\/$/, "")}/prompt-questions`;

    // Use provided accessToken or fall back to silent acquisition
    let token = accessToken;
    if (!token) {
      token = (await acquireAccessToken()) || undefined;
    }

    // If we have a token, pass it in headers explicitly
    if (token) {
      const resp = await axiosClient.get(url, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      return resp.data;
    }

    // Otherwise rely on interceptor
    const resp = await axiosClient.get(url);
    return resp.data;
  },

  // Enhance a user prompt (GET with query param). Requires Bearer token.
  enhancePrompt: async (prompt: string, accessToken?: string) => {
    const base = ENHANCE_API_BASE_URL.replace(/\/$/, "");
    const encoded = encodeURIComponent(prompt);
    const url = `${base}/enhance-prompt?prompt=${encoded}`;
    // Use axiosClient for consistency (interceptor handles token)
    const resp = await axiosClient.get(url);
    return resp.data;
  },
};

export type {
  CreateMessagePayload,
  CreateFirstMessagePayload,
  CreateSubsequentMessagePayload,
};
